import isEmpty from 'lodash.isempty'
import isEqual from 'lodash.isequal'
import { ListingStrategy } from '../../types/api'
import { TemplateFormItem } from '../products_v5/components/JoinedForm/types'
import { SgwForm } from './types'

// Prefer the most specific strategy, in terms of how many categories it has mapped.
// Less categories == more specific == a better default choice.
// Note, this is the opposite of how classic list (JoinedForm) works.
export function getPreferredStrategy(listingStrategies: ListingStrategy[]) {
  if (listingStrategies.length === 0) {
    // eslint-disable-next-line no-throw-literal
    throw 'calling code should check that listingStrategies.length > 0'
  }

  const preferredStrategy = listingStrategies
    .filter((s: ListingStrategy) => s.categories.length > 0)
    .sort((s1, s2) => s1.categories.length - s2.categories.length)[0]

  // listingStrategies[0] - You may only have strategies that are mapped to 0 categories, such as "General Strategy No Categories"
  return preferredStrategy || listingStrategies[0]
}

// feel free to expand this over time, like if you need to check objects
type AnythingButNumberOrBoolean = string | any[]

// lodash isEmpty, except the compiler prevents you from passing in numbers (a common footgun).
// Lodash maintainers think numbers and booleans are always empty 🙃 https://github.com/lodash/lodash/issues/496
// Calling code should call numberVar.toString() on numeric parameters.
// Calling code should handle boolean parameters in another way.
export function safeIsEmpty(v: AnythingButNumberOrBoolean) {
  return isEmpty(v)
}

export type ExpressListProductState = {
  title: string
  description: string
  category?: { id?: number | null }
  images?: unknown[]
  tags?: string[]
  notes?: { body: string }[]
  draft?: boolean
  sku?: string | null
  length?: number | null
  width?: number | null
  height?: number | null
  weight?: number | null
  shippingWeight?: string | null
  shippingBox?: { id: number } | null
  supplier?: { id: number } | null
  boxPadding?: string
  inventoryLocation?: unknown
}

export function createEmptyExpressListProduct(): ExpressListProductState {
  return {
    title: '',
    description: '',
    category: { id: null },
    images: [],
    tags: [],
    notes: [],
    draft: true
  }
}

export type ExpressListFormSnapshot = {
  title: string
  description: string
  categoryId: number | null
  strategyId: number | null
  tags: string[]
  images: { id?: number; removed: boolean; rotation: number }[]
  weight: string
  shippingWeight: string | null
  length: string
  width: string
  height: string
  shippingBoxId: number | undefined
  boxPadding: number | null
  inventoryLocationId: number | null
  supplierId: number | null
  additionalNote: string
  sgwForm: SgwForm
  templateForm: { productField?: string; type?: string; value: unknown }[] | null
}

export type ExpressListFormSnapshotInput = {
  title: string
  description: string
  categoryId: number | null
  strategyId: number | null
  tags: string[]
  images: unknown[]
  weight: string
  shippingWeight: string | null
  length: string
  width: string
  height: string
  shippingBoxId: number | undefined
  boxPadding: number | null
  inventoryLocationId: number | null
  supplierId: number | null
  additionalNote: string
  sgwForm: Record<string, unknown>
  templateForm: TemplateFormItem[] | null
}

const serializeSgwForm = (sgwForm: Record<string, unknown>): SgwForm => {
  const { oldCategoryConfig: _oldCategoryConfig, ...rest } = sgwForm
  return rest as SgwForm
}

const serializeTemplateForm = (templateForm: TemplateFormItem[] | null) =>
  templateForm?.map(item => ({
    productField: item.productField,
    type: item.type,
    value: item.props?.value
  })) ?? null

const serializeImages = (images: unknown[]) =>
  (images || []).map((image: { id?: number; removed?: boolean; rotation?: number }) => ({
    id: image?.id,
    removed: !!image?.removed,
    rotation: (image?.rotation ?? 0) % 360
  }))

export function getExpressListFormSnapshot(input: ExpressListFormSnapshotInput): ExpressListFormSnapshot {
  return {
    title: input.title,
    description: input.description,
    categoryId: input.categoryId,
    strategyId: input.strategyId,
    tags: input.tags,
    images: serializeImages(input.images),
    weight: input.weight,
    shippingWeight: input.shippingWeight,
    length: input.length,
    width: input.width,
    height: input.height,
    shippingBoxId: input.shippingBoxId,
    boxPadding: input.boxPadding,
    inventoryLocationId: input.inventoryLocationId,
    supplierId: input.supplierId,
    additionalNote: input.additionalNote,
    sgwForm: serializeSgwForm(input.sgwForm),
    templateForm: serializeTemplateForm(input.templateForm)
  }
}

export function isExpressListFormDirty(baseline: ExpressListFormSnapshot, current: ExpressListFormSnapshot): boolean {
  return !isEqual(baseline, current)
}
